import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Mail, Plus, ArrowRight } from 'lucide-react'
import { Card, CardContent, Button } from '@/components/ui'
import { LoadingSpinner } from '@/components/shared'
import { useFamily, usePendingInvitations } from '@/hooks'

export function OnboardingPage() {
  const navigate = useNavigate()
  const { families, isLoading: isFamiliesLoading } = useFamily()
  const { invitations, isLoading: isInvitationsLoading } = usePendingInvitations()

  const isLoading = isFamiliesLoading || isInvitationsLoading
  const hasFamilies = families.length > 0
  const hasPendingInvitations = invitations.length > 0

  // Redirect to dashboard if user already has families
  useEffect(() => {
    if (!isLoading && hasFamilies) {
      navigate('/')
    }
  }, [isLoading, hasFamilies, navigate])

  // If user has pending invitations, show them that page instead
  useEffect(() => {
    if (!isLoading && !hasFamilies && hasPendingInvitations) {
      navigate('/onboarding/invitations')
    }
  }, [isLoading, hasFamilies, hasPendingInvitations, navigate])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-gray-500">Getting everything ready for you...</p>
        </div>
      </div>
    )
  }

  // If we have families or invitations, we'll be redirected
  if (hasFamilies || hasPendingInvitations) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-lg px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
            <Users className="h-8 w-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome to FamSteady</h1>
          <p className="mt-1 text-gray-500">
            We're here to help you care for the people who matter most
          </p>
        </div>

        {/* Options */}
        <div className="space-y-4">
          {/* Create family option */}
          <Card className="cursor-pointer transition-shadow hover:shadow-md">
            <CardContent
              className="p-6"
              onClick={() => navigate('/onboarding/create-family')}
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100">
                  <Plus className="h-6 w-6 text-primary-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Start your care circle</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Create a family group and invite the people you love. You'll be the one keeping everything running smoothly.
                  </p>
                  <Button variant="link" className="mt-2 h-auto p-0">
                    Let's do it <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Join family option */}
          <Card className="cursor-pointer transition-shadow hover:shadow-md">
            <CardContent
              className="p-6"
              onClick={() => navigate('/onboarding/invitations')}
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-success-50">
                  <Mail className="h-6 w-6 text-success-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">I've been invited</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Someone's already set things up for you! Check your email for an invite link, or see if there's one waiting.
                  </p>
                  <Button variant="link" className="mt-2 h-auto p-0 text-success-600">
                    Check for invitations <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Help text */}
        <div className="mt-8 rounded-lg bg-primary-50 p-4 text-sm text-gray-700">
          <p className="font-medium text-gray-900">Not sure which to choose?</p>
          <p className="mt-1 text-gray-600">
            If you're the one setting things up to keep an eye on a parent or family member, go with "Start your care circle". If someone else is handling the setup and invited you, choose "I've been invited".
          </p>
        </div>
      </div>
    </div>
  )
}
